import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  TextField,
  MenuItem,
} from '@mui/material';
import { franchiseEnquiryAPI } from '../../services/api';
import { toast } from 'react-toastify';

import LandingNavbar from '../../components/landing/LandingNavbar';
import HeroSection from '../../components/landing/HeroSection';
import PartnerStrip from '../../components/landing/PartnerStrip';
import AffiliationsSection from '../../components/landing/AffiliationsSection';
import StatsSection from '../../components/landing/StatsSection';
import WhyChooseUs from '../../components/landing/WhyChooseUs';
import CoursesCarousel from '../../components/landing/CoursesCarousel';
import CourseGroupsSection from '../../components/landing/CourseGroupsSection';
import GallerySection from '../../components/landing/GallerySection';
import TestimonialsSection from '../../components/landing/TestimonialsSection';
import CtaBanner from '../../components/landing/CtaBanner';
import ContactSection from '../../components/landing/ContactSection';
import FooterSection from '../../components/landing/FooterSection';
import FloatingAiCoursesButton from '../../components/landing/FloatingAiCoursesButton';

const dialogPaperSx = {
  bgcolor: '#0d1627',
  color: '#fff',
  borderRadius: 4,
  border: '1px solid rgba(255,255,255,0.08)',
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#38bdf8' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#38bdf8' },
  '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.6)' },
};

const districts = [
  'Thiruvananthapuram', 'Kollam', 'Pathanamthitta', 'Alappuzha',
  'Kottayam', 'Idukki', 'Ernakulam', 'Thrissur',
  'Palakkad', 'Malappuram', 'Kozhikode', 'Wayanad', 'Kannur', 'Kasargod',
];

const emptyFranchise = {
  name: '', institutionName: '', email: '', phone: '',
  city: '', place: '', district: '', address: '', pincode: '', message: '',
};

export default function LandingPage() {
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [franchiseOpen, setFranchiseOpen] = useState(false);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [franchiseForm, setFranchiseForm] = useState(emptyFranchise);

  const handleFranchiseChange = (e) =>
    setFranchiseForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleFranchiseSubmit = async (e) => {
    e.preventDefault();
    try {
      await franchiseEnquiryAPI.create(franchiseForm);
      toast.success('Franchise enquiry submitted! We will contact you soon.');
      setFranchiseOpen(false);
      setFranchiseForm(emptyFranchise);
    } catch (error) {
      console.error('Error submitting franchise enquiry:', error);
      toast.error(error.response?.data?.message || 'Failed to submit enquiry. Please try again.');
    }
  };

  return (
    <Box sx={{ bgcolor: '#f8faff', minHeight: '100vh' }}>
      <LandingNavbar
        onEnroll={() => setEnrollOpen(true)}
        onFranchise={() => setFranchiseOpen(true)}
      />
      <HeroSection onEnroll={() => setEnrollOpen(true)} />
      <CourseGroupsSection />
      <PartnerStrip />
      <AffiliationsSection />
      <StatsSection />
      <WhyChooseUs />
      <CoursesCarousel />
      <GallerySection />
      <TestimonialsSection />
      <CtaBanner onEnroll={() => setEnrollOpen(true)} />
      <ContactSection />
      <FooterSection />
      <FloatingAiCoursesButton />

      {/* Enroll Dialog */}
      <Dialog
        open={enrollOpen}
        onClose={() => setEnrollOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, color: '#38bdf8' }}>
          Ready to Enroll?
        </DialogTitle>
        <DialogContent>
          <Typography align="center" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            You are about to be redirected to our Student Registration portal. Need help first?
            Use "Request Call" and our team will assist you.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
          <Button
            onClick={() => { setEnrollOpen(false); window.location.href = '#contact'; }}
            variant="outlined"
            sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', borderRadius: 2 }}
          >
            Request Call
          </Button>
          <Button
            onClick={() => { setEnrollOpen(false); setComingSoonOpen(true); }}
            variant="contained"
            sx={{ background: 'linear-gradient(135deg,#1d6cf0,#0ea5e9)', borderRadius: 2 }}
          >
            Proceed to Registration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Coming Soon Dialog */}
      <Dialog
        open={comingSoonOpen}
        onClose={() => setComingSoonOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, color: '#38bdf8' }}>
          Coming Soon!
        </DialogTitle>
        <DialogContent>
          <Typography align="center" variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#fff' }}>
            Online registration is on the way
          </Typography>
          <Typography align="center" color="rgba(255,255,255,0.6)" sx={{ mb: 2 }}>
            We are working hard to bring you online registration. This feature will be available soon!
          </Typography>
          <Typography align="center" variant="body2" color="rgba(255,255,255,0.4)">
            In the meantime, use the "Request Call" option and our team will assist you.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={() => setComingSoonOpen(false)}
            variant="contained"
            sx={{ background: 'linear-gradient(135deg,#1d6cf0,#0ea5e9)', borderRadius: 2, px: 4 }}
          >
            Got It
          </Button>
        </DialogActions>
      </Dialog>

      {/* Franchise Dialog */}
      <Dialog
        open={franchiseOpen}
        onClose={() => setFranchiseOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, color: '#38bdf8' }}>
          Franchise Enquiry
        </DialogTitle>
        <DialogContent>
          <Typography align="center" sx={{ mb: 3, color: 'rgba(255,255,255,0.6)' }}>
            Join BSS IT Mission as a franchise partner. Fill in your details and we'll get back to you.
          </Typography>
          <Box component="form" onSubmit={handleFranchiseSubmit} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Full Name" name="name"
                  value={franchiseForm.name} onChange={handleFranchiseChange}
                  required sx={inputSx} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Name of Center / Institution / College"
                  name="institutionName" value={franchiseForm.institutionName}
                  onChange={handleFranchiseChange} required sx={inputSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email Address" name="email" type="email"
                  value={franchiseForm.email} onChange={handleFranchiseChange}
                  required sx={inputSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone Number" name="phone"
                  value={franchiseForm.phone} onChange={handleFranchiseChange}
                  required sx={inputSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="City" name="city"
                  value={franchiseForm.city} onChange={handleFranchiseChange}
                  required sx={inputSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Place" name="place"
                  value={franchiseForm.place} onChange={handleFranchiseChange}
                  required sx={inputSx} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth select label="District" name="district"
                  value={franchiseForm.district} onChange={handleFranchiseChange}
                  required sx={inputSx}
                  SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: '#0d1627', color: '#fff' } } } }}
                >
                  {districts.map((d) => (
                    <MenuItem key={d} value={d} sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(56,189,248,0.1)' } }}>
                      {d}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Full Address" name="address"
                  value={franchiseForm.address} onChange={handleFranchiseChange}
                  required multiline rows={2} placeholder="Enter complete address..."
                  sx={inputSx} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Pincode" name="pincode"
                  value={franchiseForm.pincode} onChange={handleFranchiseChange}
                  required inputProps={{ maxLength: 6, pattern: '[0-9]{6}' }}
                  placeholder="6-digit pincode" sx={inputSx} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Message (Optional)" name="message"
                  value={franchiseForm.message} onChange={handleFranchiseChange}
                  multiline rows={3}
                  placeholder="Tell us about your interest in becoming a franchise partner..."
                  sx={inputSx} />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3, gap: 1 }}>
          <Button
            onClick={() => setFranchiseOpen(false)}
            variant="outlined"
            sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', borderRadius: 2, minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFranchiseSubmit}
            variant="contained"
            sx={{ background: 'linear-gradient(135deg,#1d6cf0,#0ea5e9)', borderRadius: 2, minWidth: 100 }}
          >
            Submit Enquiry
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
